/**
 * Access-state tokens and labels for a partner office.
 *
 * Kept out of the component file so react-refresh stays happy and the label
 * logic is unit-testable - the same split as ``currency-options.ts``.
 *
 * The vocabulary is deliberate. The ACCESS badge answers "can this person get
 * into this office"; it does NOT report whether the paperwork behind that
 * access is complete. A grant with no staff record still works, so it reads
 * "Has access" and the missing record is flagged in the staff column instead.
 * A filter pill is a different job - it names the group being filtered - so an
 * orphaned grant is "Not on staff" there. Hence two label functions, not one.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { OfficeAccess } from '@/admin/hooks/use-network-people'

/**
 * Colour is reserved for the ONE state that needs a human.
 *
 * 310 of 326 people on a real tenant have access, so a green pill on every row
 * spent the accent on the norm and left nothing for the exception - the column
 * read as decoration. Working access is now neutral; only "no access" is
 * coloured. A missing staff record is flagged in the ON STAFF SINCE column,
 * where the missing fact actually lives, rather than by miscolouring access.
 */
export const ACCESS_TONE: Record<OfficeAccess, string> = {
  direct_grant: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  inherited: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  orphaned_grant: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  no_grant: 'bg-[var(--status-error-bg)] text-[var(--status-error)]',
  // A different problem from "no access": the partner is switched off, so the
  // fix is reactivating them, not granting anybody anything.
  suspended: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
}

/**
 * Which state wins a row's Access cell when a person's offices disagree.
 * Ordered by what needs a human: no access outranks working access.
 */
export const ACCESS_SEVERITY: Record<OfficeAccess, number> = {
  no_grant: 3,
  suspended: 2,
  inherited: 1,
  orphaned_grant: 1,
  direct_grant: 0,
}

export function useAccessLabels() {
  const { t } = useTranslation()

  /**
   * What is true of ACCESS for this state.
   *
   * Memoised so callers can list it in a dependency array without re-running
   * their memos on every render.
   */
  const accessLabel = useCallback(
    (state: OfficeAccess) =>
      ({
        direct_grant: t('network.people.accessDirectGrant', 'Has access'),
        inherited: t('network.people.accessInherited', 'Automatic'),
        no_grant: t('network.people.accessNoGrant', 'No access'),
        // Truthfully has access - the staff record is what is missing.
        orphaned_grant: t('network.people.accessDirectGrant', 'Has access'),
        suspended: t('network.people.accessSuspended', 'Partner suspended'),
      })[state],
    [t],
  )

  /** The name of the GROUP being filtered, which can differ from the above. */
  const pillLabel = useCallback(
    (state: OfficeAccess) =>
      state === 'orphaned_grant'
        ? t('network.people.accessOrphanedGrant', 'Not on staff')
        : accessLabel(state),
    [t, accessLabel],
  )

  return { accessLabel, pillLabel }
}
